/* global gtag */
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import googleAnalytics from './plugins/googleAnalytics';
import ErrorHandler from './utils/errorHandler';
import { applyPrivacyMetaTags, removeSocialMediaTags } from './utils/privacyMeta';

// Validate environment variables before starting the application
import { validateFrontendEnvironmentOrThrow } from './utils/environmentValidator';

// Perform environment validation - this will throw and prevent app startup if validation fails
try {
  console.log('🔍 Validating frontend environment variables...');
  validateFrontendEnvironmentOrThrow();
  console.log('✅ Frontend environment validation passed');
} catch (error) {
  console.error('❌ Frontend environment validation failed:');
  console.error(error.details?.formattedResults || error.message);
  console.error('\n💡 Please check your .env file against .env.example');
  // In frontend, we could show an error overlay instead of throwing
  document.body.innerHTML = `
    <div style="padding: 20px; background: #fee; color: #c33; font-family: monospace;">
      <h2>Environment Configuration Error</h2>
      <pre>${error.details?.formattedResults || error.message}</pre>
      <p>Please check your .env file against .env.example and refresh the page.</p>
    </div>
  `;
  throw error;
}

const app = createApp(App);
const pinia = createPinia();

// Global error handler
app.config.errorHandler = (error, vm, info) => {
  ErrorHandler.handleComponentError(error, vm, info);
};

// Global warning handler (development only)
if (process.env.VUE_APP_DEBUG === 'true') {
  app.config.warnHandler = (msg, vm, trace) => {
    console.warn(`Vue Warning: ${msg}`, { vm, trace });
  };
}

// Router error handling
router.onError((error, to, from) => {
  ErrorHandler.handleRouteError(error, to, from);
});

app.use(pinia);
app.use(router);
app.use(googleAnalytics);

// Apply privacy meta tags immediately on app startup
if (typeof document !== 'undefined') {
  applyPrivacyMetaTags();
  removeSocialMediaTags();
}

// Track page views after every route change
router.afterEach((to) => {
  if (process.env.VUE_APP_ENV === 'production') {
    if (typeof gtag === 'function') {
      gtag('config', process.env.VUE_APP_GA_ID, { page_path: to.fullPath });
    }
  }
});

app.mount('#app');